import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import analyticsService, { CreateABTestPayload } from '../../../services/analyticsService';
import emailService from '../../../services/emailService';
import toast from 'react-hot-toast';

interface Campaign {
  id: string;
  name: string;
}

interface Template {
  id: string;
  name: string;
  subject: string;
  body: string;
}

// Helper function to map EmailTemplate to Template (content to body)
const mapToTemplate = (template: any): Template => ({
  id: template.id || '',
  name: template.name,
  subject: template.subject,
  body: template.content // Map content to body
});

// Helper function to map EmailCampaign to Campaign
const mapToCampaign = (campaign: any): Campaign => ({
  id: campaign.id || '',
  name: campaign.name
});

const EmailABTestCreate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [testName, setTestName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [campaignId, setCampaignId] = useState<string>('');
  const [testType, setTestType] = useState<'subject' | 'content' | 'sender_name' | 'send_time'>('subject');
  const [winnerMetric, setWinnerMetric] = useState<'open_rate' | 'click_rate' | 'reply_rate' | 'conversion_rate'>('open_rate');
  const [variants, setVariants] = useState<Array<{
    id: string;
    name: string;
    templateId: string;
    subject: string;
    body: string;
    isControl: boolean;
    distributionPercentage: number;
  }>>([
    {
      id: '1',
      name: 'Control',
      templateId: '',
      subject: '',
      body: '',
      isControl: true,
      distributionPercentage: 50
    },
    {
      id: '2',
      name: 'Variant 1',
      templateId: '',
      subject: '',
      body: '',
      isControl: false,
      distributionPercentage: 50
    }
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [campaignsData, templatesData] = await Promise.all([
          emailService.getCampaigns(),
          emailService.getTemplates()
        ]);
        
        // Map data to the expected interfaces
        const mappedCampaigns = (campaignsData || []).map(mapToCampaign);
        const mappedTemplates = (templatesData || []).map(mapToTemplate);
        
        setCampaigns(mappedCampaigns);
        setTemplates(mappedTemplates);
      } catch (err) {
        console.error('Error loading data:', err);
        toast.error('Failed to load required data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddVariant = () => {
    if (variants.length >= 5) {
      toast.error('Maximum of 5 variants allowed');
      return;
    }

    setVariants([
      ...variants,
      {
        id: `${variants.length + 1}`,
        name: `Variant ${variants.length}`,
        templateId: '',
        subject: '',
        body: '',
        isControl: false,
        distributionPercentage: Math.floor(100 / (variants.length + 1))
      }
    ]);

    // Rebalance distribution percentages
    rebalanceDistribution();
  };

  const handleRemoveVariant = (index: number) => {
    if (variants.length <= 2) {
      toast.error('Minimum of 2 variants required');
      return;
    }

    if (variants[index].isControl) {
      toast.error('Cannot remove control variant');
      return;
    }

    const newVariants = variants.filter((_, i) => i !== index);
    setVariants(newVariants);

    // Rebalance distribution percentages
    setTimeout(() => {
      rebalanceDistribution();
    }, 0);
  };

  const rebalanceDistribution = () => {
    const equalShare = Math.floor(100 / variants.length);
    const remainder = 100 - (equalShare * variants.length);
    
    const newVariants = variants.map((variant, i) => ({
      ...variant,
      distributionPercentage: equalShare + (i === 0 ? remainder : 0)
    }));
    
    setVariants(newVariants);
  };

  const handleVariantChange = (index: number, field: string, value: any) => {
    const newVariants = [...variants];
    
    // @ts-ignore
    newVariants[index][field] = value;
    
    // If changing template, update subject and body
    if (field === 'templateId') {
      const template = templates.find(t => t.id === value);
      if (template) {
        newVariants[index].subject = template.subject;
        newVariants[index].body = template.body;
      }
    }
    
    // Update control status if needed
    if (field === 'isControl' && value === true) {
      newVariants.forEach((v, i) => {
        if (i !== index) {
          v.isControl = false;
        }
      });
    }
    
    setVariants(newVariants);
  };

  const handleDistributionChange = (index: number, value: number) => {
    if (value < 1 || value > 99) {
      return;
    }
    
    const newVariants = [...variants];
    const currentTotal = newVariants.reduce((sum, v, i) => i === index ? sum : sum + v.distributionPercentage, 0);
    const available = 100 - currentTotal;
    
    if (value > available) {
      toast.error(`Distribution cannot exceed ${available}%`);
      return;
    }
    
    newVariants[index].distributionPercentage = value;
    setVariants(newVariants);
  };

  const validateForm = (): boolean => {
    if (!testName.trim()) {
      toast.error('Test name is required');
      return false;
    }

    if (!campaignId) {
      toast.error('Campaign selection is required');
      return false;
    }

    const totalDistribution = variants.reduce((sum, v) => sum + v.distributionPercentage, 0);
    if (totalDistribution !== 100) {
      toast.error(`Total distribution must equal 100% (current: ${totalDistribution}%)`);
      return false;
    }

    const hasControl = variants.some(v => v.isControl);
    if (!hasControl) {
      toast.error('One variant must be designated as the control');
      return false;
    }

    const validTemplates = variants.every(v => !!v.templateId);
    if (!validTemplates) {
      toast.error('All variants must have a template selected');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const payload: CreateABTestPayload = {
        name: testName,
        description: description || undefined,
        campaignId,
        testType,
        winnerMetric,
        variants: variants.map(v => ({
          name: v.name,
          templateId: v.templateId,
          subject: v.subject,
          body: v.body,
          isControl: v.isControl,
          distributionPercentage: v.distributionPercentage
        }))
      };

      const result = await analyticsService.createABTest(payload);
      toast.success('A/B Test created successfully');
      navigate(`/email/ab-tests/${result.id}`);
    } catch (err: any) {
      console.error('Error creating A/B test:', err);
      toast.error(err.message || 'Failed to create A/B test');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/email/ab-tests')}
          className="mb-2 flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to A/B Tests
        </button>
        <h2 className="text-xl font-semibold text-gray-800">Create New A/B Test</h2>
      </div>

      <div className="px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Test Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Test Name *</label>
                  <input
                    type="text"
                    value={testName}
                    onChange={(e) => setTestName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Subject Line Test - July 2025"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign *</label>
                  <select
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select a Campaign</option>
                    {campaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the purpose of this test"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Test Type *</label>
                  <select
                    value={testType}
                    onChange={(e) => setTestType(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="subject">Subject Line</option>
                    <option value="content">Email Content</option>
                    <option value="sender_name">Sender Name</option>
                    <option value="send_time">Send Time</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Winner Metric *</label>
                  <select
                    value={winnerMetric}
                    onChange={(e) => setWinnerMetric(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="open_rate">Open Rate</option>
                    <option value="click_rate">Click Rate</option>
                    <option value="reply_rate">Reply Rate</option>
                    <option value="conversion_rate">Conversion Rate</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Test Variants</h3>
                <button
                  type="button"
                  onClick={handleAddVariant}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none text-sm"
                >
                  Add Variant
                </button>
              </div>

              {variants.map((variant, index) => (
                <div key={variant.id} className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <div className="flex justify-between">
                    <div className="flex items-center space-x-3">
                      <h4 className="font-medium">Variant {index + 1}</h4>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`control-${index}`}
                          checked={variant.isControl}
                          onChange={(e) => handleVariantChange(index, 'isControl', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`control-${index}`} className="ml-2 text-sm text-gray-700">
                          Control
                        </label>
                      </div>
                    </div>
                    {!variant.isControl && (
                      <button
                        type="button"
                        onClick={() => handleRemoveVariant(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Variant Name</label>
                      <input
                        type="text"
                        value={variant.name}
                        onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Control Group"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Distribution (%)</label>
                      <div className="flex items-center">
                        <input
                          type="number"
                          min="1"
                          max="99"
                          value={variant.distributionPercentage}
                          onChange={(e) => handleDistributionChange(index, parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span className="ml-2">%</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Template *</label>
                    <select
                      value={variant.templateId}
                      onChange={(e) => handleVariantChange(index, 'templateId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select a Template</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>{template.name}</option>
                      ))}
                    </select>
                  </div>

                  {testType === 'subject' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
                      <input
                        type="text"
                        value={variant.subject}
                        onChange={(e) => handleVariantChange(index, 'subject', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter email subject"
                        required
                      />
                    </div>
                  )}

                  {testType === 'content' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Content *</label>
                      <textarea
                        value={variant.body}
                        onChange={(e) => handleVariantChange(index, 'body', e.target.value)}
                        rows={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter email body content"
                        required
                      />
                    </div>
                  )}

                  {testType === 'sender_name' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sender Name *</label>
                      <input
                        type="text"
                        value={variant.body}
                        onChange={(e) => handleVariantChange(index, 'body', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., John Smith, CEO"
                        required
                      />
                    </div>
                  )}

                  {testType === 'send_time' && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Send Time *</label>
                      <input
                        type="text"
                        value={variant.body}
                        onChange={(e) => handleVariantChange(index, 'body', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Morning (9 AM), Evening (6 PM)"
                        required
                      />
                    </div>
                  )}
                </div>
              ))}

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={() => navigate('/email/ab-tests')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create A/B Test'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EmailABTestCreate;
